
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { useIsMobile } from "../../hooks/use-mobile";

export const Hero = () => {
  const [activeBannerMessage, setActiveBannerMessage] = useState(0);
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const interval = window.setInterval(() => {
      setActiveBannerMessage((current) => (current + 1) % 2);
    }, 4500);

    return () => window.clearInterval(interval);
  }, []);

  return <section className="relative min-h-[100svh] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="h-full w-full bg-gradient-to-br from-[#FFFF4D] via-[#FFE94B] to-[#A3D960]"></div>
        {!isMobile && <div className="absolute top-0 right-0 h-full w-1/4 md:w-1/4 opacity-60 md:opacity-80 flex items-end">
            <img src="/lovable-uploads/180f51c0-05cf-4dfd-b4b7-91b153baf486.png" alt="Décoration" className="h-auto max-h-[70vh] w-full object-contain object-bottom drop-shadow-xl filter brightness-110 contrast-110" />
          </div>}
        {isMobile && <div className="absolute inset-0 flex justify-end items-start pt-24 opacity-15">
            <img src="/lovable-uploads/180f51c0-05cf-4dfd-b4b7-91b153baf486.png" alt="Décoration en arrière-plan" className="h-auto max-h-[60vh] object-contain object-right" />
          </div>}
        <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-[#FCACF4] blur-3xl opacity-20"></div>
        <div className="absolute top-1/3 left-0 w-24 h-24 rounded-full bg-[#39CCFF] blur-3xl opacity-20"></div>
        <div className="absolute bottom-1/4 right-1/4 w-32 h-32 rounded-full bg-[#FCACF4] blur-3xl opacity-15"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FCACF4] to-transparent opacity-60"></div>
      </div>
      
      <div className="container relative z-10 mt-20 sm:mt-8 md:mt-16 lg:mt-24 px-4 md:px-6">
        <div className="mb-6 md:mb-10 lg:mb-16 animate-fadeInUp mx-0">
          <div className="w-full mb-10 md:mb-14">
            <div className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-[#15153a] via-[#1E3A8A] to-[#4c51bf] px-4 sm:px-6 py-4 sm:py-5 md:py-6 shadow-lg transition-all hover:shadow-xl hover:scale-[1.01]">
              {/* Accent diagonal rouge/rose inspiré de l'affiche */}
              <div className="absolute -right-6 -top-6 h-32 w-32 sm:h-36 sm:w-36 rotate-12 bg-[#e11d48] opacity-90 transition-transform group-hover:rotate-6" />

              <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3">
                <Link to="/actions" className="shrink-0 text-center sm:text-left">
                  <p className="inline-block text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[#15153a] bg-white px-2 py-0.5 rounded-full mb-1">
                    Soirée Exceptionnelle
                  </p>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-white leading-none">
                    À Haute VOIX
                  </h3>
                  <p className="text-xs sm:text-sm text-white/90 mt-0.5">
                    25 · 11 · 2026 — Théâtre de la Tour Eiffel
                  </p>
                </Link>

                <Link to="/actions" className="relative min-h-[80px] flex-1 overflow-hidden sm:min-h-[90px] md:min-h-[100px]" aria-live="polite">
                  <div className={`absolute inset-0 flex items-center justify-center px-4 py-2 text-center transition-all duration-700 ${activeBannerMessage === 0 ? "translate-y-0 opacity-100" : "-translate-y-3 opacity-0 pointer-events-none"}`}>
                    <p className="max-w-xl text-base sm:text-lg md:text-xl lg:text-2xl font-bold italic leading-snug text-white">
                      Promouvoir notre socle civique
                    </p>
                  </div>
                  <div className={`absolute inset-0 flex items-center justify-center px-4 py-2 text-center transition-all duration-700 ${activeBannerMessage === 1 ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0 pointer-events-none"}`}>
                    <p className="max-w-3xl text-xs sm:text-sm md:text-base lg:text-lg font-medium italic leading-snug text-white">
                      Des personnalités de premier plan de la société civile et des invités exceptionnels prendront la parole (liste définitive le 10 novembre),<br className="hidden sm:block" /> les débats seront animés par Fabrice Lundy
                    </p>
                  </div>
                </Link>

                <div className="flex flex-col gap-2 shrink-0 self-center">
                  <Link to="/actions" className="group/btn flex items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20">
                    <span>Découvrir</span>
                    <ArrowRight size={18} className="transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                  <Link to="/inscription-AHV2026" className="group/btn flex items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20">
                    <span>Se préinscrire</span>
                    <ArrowRight size={18} className="transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <h1 className={`text-3xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-3 mx-auto text-center tracking-tight leading-tight uppercase text-[#1E3A8A]`}>
            FAISONS VIVRE ENSEMBLE<br />NOS VALEURS !
          </h1>
          <h2 className="text-xl sm:text-xl md:text-2xl mb-4 max-w-4xl mx-auto text-center tracking-tight font-light text-[#1E3A8A]">
            <span>Liberté</span> <span className="mx-2">•</span> 
            <span>Égalité</span> <span className="mx-2">•</span> 
            <span>Fraternité</span> <span className="mx-2">•</span> 
            <span>Laïcité</span>
          </h2>
          
          <div className="w-24 h-1 bg-[#1E3A8A] mx-auto my-4 rounded-full"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 mt-2 md:mt-0">
          <div className="bg-gray-200/80 backdrop-blur-md border border-black/20 p-4 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] h-full flex flex-col relative">
            <div className="flex flex-col justify-center items-center flex-1 text-center">
              <h3 className="text-base font-semibold mb-2 text-[#1E3A8A]">Signer La Charte</h3>
              <div className="flex-1 flex items-center">
                <p className="text-[#1E3A8A] text-sm mb-3">Soutenez l'action des Enfants de la République en signant La Charte. Vous recevrez votre charte personnalisée. </p>
              </div>
              <div className="mt-auto flex justify-center">
                <Link to="/charte-republicaine" className="inline-flex items-center justify-center bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-4 py-2 rounded-lg font-medium transition-colors group">
                  <span>Consulter la charte</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-gray-200/80 backdrop-blur-md border border-black/20 p-4 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] h-full flex flex-col relative">
            <div className="flex flex-col justify-center items-center flex-1 text-center">
              <h3 className="text-base font-semibold mb-2 text-[#1E3A8A]">Adhérer à nos valeurs communes</h3>
              <p className="text-[#1E3A8A] text-sm mb-3">En devenant membre, vous faites partie des forces vives pour mener les actions à venir : vous pouvez participer concrètement au réengagement de l'ensemble des citoyens pour leur pays.</p>
              <div className="mt-auto flex justify-center">
                <Link to="/adhesion" className="inline-flex items-center justify-center bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-4 py-2 rounded-lg font-medium transition-colors group">
                  <span>Devenir membre</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-gray-200/80 backdrop-blur-md border border-black/20 p-4 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] h-full flex flex-col relative">
            <div className="flex flex-col justify-between items-center h-full text-center">
              <h3 className="text-base font-semibold mb-2 text-[#1E3A8A]">Suivre nos actions</h3>
              <div className="flex-1 flex items-center">
                <p className="text-[#1E3A8A] text-sm">Découvrez les actions menées par notre association à travers la France.</p>
              </div>
              <div className="mt-4 flex justify-center">
                <Link to="/actions" className="inline-flex items-center justify-center bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-4 py-2 rounded-lg font-medium transition-colors group">
                  <span>Voir nos actions</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
